import { Check, Clock, X } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCancelManualTime, useReviewManualTime, type ManualTimeRequest } from "@/api/queries";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { EmptyState } from "@/components/common/EmptyState";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { errorText } from "@/lib/errors";
import { dateInZone, formatDuration, formatShortDate, formatShortDateTime, formatTime } from "@/lib/format";

import { ManualStatusBadge } from "./ManualStatus";

type Action = { kind: "approve" | "reject" | "cancel"; item: ManualTimeRequest };

type ManualTimeListProps = {
  items: ManualTimeRequest[] | undefined;
  /** Time zone the intervals are shown in. */
  timeZone: string;
  showUser?: boolean;
  empty: { title: string; description?: string };
};

/** Manual time requests with their status and the actions the caller may take. */
export function ManualTimeList({ items, timeZone, showUser, empty }: ManualTimeListProps) {
  const { t } = useTranslation();
  const [action, setAction] = useState<Action | null>(null);

  if (!items) {
    return (
      <div className="flex flex-col gap-2 p-5">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }
  if (items.length === 0) {
    return <EmptyState icon={Clock} title={empty.title} description={empty.description} />;
  }
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left">
          <thead className="bg-surface-muted text-xs text-muted">
            <tr>
              {showUser && <th className="px-5 py-2.5 font-medium">{t("manual.columns.employee")}</th>}
              <th className="px-5 py-2.5 font-medium">{t("manual.columns.when")}</th>
              <th className="px-5 py-2.5 font-medium">{t("manual.columns.project")}</th>
              <th className="px-5 py-2.5 font-medium">{t("manual.columns.reason")}</th>
              <th className="px-5 py-2.5 font-medium">{t("manual.columns.status")}</th>
              <th className="px-5 py-2.5">
                <span className="sr-only">{t("manual.columns.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <Row key={item.id} item={item} timeZone={timeZone} showUser={showUser} onAction={setAction} />
            ))}
          </tbody>
        </table>
      </div>
      {action && <ActionDialog action={action} timeZone={timeZone} onClose={() => setAction(null)} />}
    </>
  );
}

function span(item: ManualTimeRequest, lang: string, timeZone: string): { date: string; range: string } {
  const startDate = dateInZone(item.started_at, timeZone);
  const endDate = dateInZone(new Date(Date.parse(item.ended_at) - 1), timeZone);
  const end = formatTime(item.ended_at, lang, timeZone);
  return {
    date: formatShortDate(startDate, lang),
    range: `${formatTime(item.started_at, lang, timeZone)}–${endDate === startDate ? end : `${end} (+1)`}`,
  };
}

function Row({
  item,
  timeZone,
  showUser,
  onAction,
}: {
  item: ManualTimeRequest;
  timeZone: string;
  showUser?: boolean;
  onAction: (a: Action) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const { date, range } = span(item, lang, timeZone);
  return (
    <tr className="border-t align-top">
      {showUser && (
        <td className="px-5 py-3 whitespace-nowrap">
          <span className="flex items-center gap-2.5 font-medium">
            <UserAvatar name={item.user.full_name} className="size-7" />
            {item.user.full_name}
          </span>
        </td>
      )}
      <td className="px-5 py-3 whitespace-nowrap">
        <div className="font-medium">{date}</div>
        <div className="text-muted tabular-nums">
          {range} · {formatDuration(item.seconds, lang)}
        </div>
      </td>
      <td className="min-w-48 px-5 py-3">
        <span className="flex items-center gap-1.5 font-medium">
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: item.project.color }}
            aria-hidden="true"
          />
          {item.project.name}
        </span>
        <div className="pl-3.5 text-muted">{item.task?.title ?? t("manual.noTask")}</div>
      </td>
      <td className="max-w-xs min-w-48 px-5 py-3 break-words">
        {item.reason}
        {item.added_by_admin && <div className="text-xs text-muted">{t("manual.addedByAdmin")}</div>}
      </td>
      <td className="max-w-52 px-5 py-3">
        <ManualStatusBadge status={item.status} />
        {item.reviewer && item.status !== "pending" && item.reviewer.id !== item.user.id && (
          <div className="mt-1 text-xs text-muted">
            {item.reviewer.full_name}
            {item.reviewed_at && `, ${formatShortDateTime(item.reviewed_at, lang, timeZone)}`}
          </div>
        )}
        {item.reviewer_comment && (
          <div className="mt-1 text-xs break-words text-muted">«{item.reviewer_comment}»</div>
        )}
      </td>
      <td className="w-px px-5 py-3 whitespace-nowrap">
        <div className="flex flex-col items-stretch gap-2">
          {item.can_review && (
            <>
              <Button variant="secondary" size="sm" onClick={() => onAction({ kind: "reject", item })}>
                <X /> {t("manual.reject")}
              </Button>
              <Button size="sm" onClick={() => onAction({ kind: "approve", item })}>
                <Check /> {t("manual.approve")}
              </Button>
            </>
          )}
          {item.can_cancel && !item.can_review && (
            <Button variant="secondary" size="sm" onClick={() => onAction({ kind: "cancel", item })}>
              {t("manual.cancel")}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function ActionDialog({
  action,
  timeZone,
  onClose,
}: {
  action: Action;
  timeZone: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const id = useId();
  const [comment, setComment] = useState("");
  const review = useReviewManualTime();
  const cancel = useCancelManualTime();
  const { item, kind } = action;
  const { date, range } = span(item, lang, timeZone);
  const summary = t("manual.dialog.summary", {
    name: item.user.full_name,
    date,
    range,
    project: item.project.name,
  });
  const labels = {
    approve: t("manual.dialog.approveTitle"),
    reject: t("manual.dialog.rejectTitle"),
    cancel: t("manual.dialog.cancelTitle"),
  };

  return (
    <ConfirmDialog
      title={labels[kind]}
      description={summary}
      confirmLabel={
        kind === "approve" ? t("manual.approve") : kind === "reject" ? t("manual.reject") : t("manual.cancel")
      }
      danger={kind !== "approve"}
      confirmDisabled={kind === "reject" && comment.trim() === ""}
      errorText={(err) => errorText(t, err)}
      onConfirm={() =>
        kind === "cancel"
          ? cancel.mutateAsync(item.id)
          : review.mutateAsync({ id: item.id, approve: kind === "approve", comment: comment.trim() })
      }
      onClose={onClose}
    >
      {kind === "cancel" ? (
        <p className="text-muted">
          {item.status === "approved" ? t("manual.dialog.cancelApproved") : t("manual.dialog.cancelPending")}
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          <p className="rounded-lg bg-surface-muted px-3 py-2 break-words">{item.reason}</p>
          <Label htmlFor={id}>
            {kind === "reject" ? t("manual.dialog.commentRequired") : t("manual.dialog.comment")}
          </Label>
          <textarea
            id={id}
            value={comment}
            maxLength={500}
            rows={3}
            autoFocus
            onChange={(e) => setComment(e.target.value)}
            className="w-full rounded-lg border bg-surface px-3 py-2 shadow-card"
          />
        </div>
      )}
    </ConfirmDialog>
  );
}
